import React, { useRef } from 'react';
import toast from 'react-hot-toast';
import { CheckCircle2, FileText, ImagePlus, Trash2 } from 'lucide-react';
import { useAssetUrl } from '../useAssetUrl.js';
import { fileProblem } from '../ui.jsx';
import NiprGuide from './NiprGuide.jsx';
import { licenseNumberOf, setLicenseNumber } from '../../../../lib/producerPage.js';

const ACCEPT = 'image/png,image/jpeg,image/webp,image/gif,application/pdf';

/** One state: Option A (certificate file, optional) — OR — Option B (license number). */
function LicenseRow({ code, name, cert, npn, number, rev, busy, onNumber, onUpload, onDelete, onRemoveState }) {
  const input = useRef(null);
  const isPdf = cert?.mime === 'application/pdf';
  const thumb = useAssetUrl('license', code, !!cert && !isPdf, rev);
  const pick = (file) => {
    const problem = fileProblem(file, { pdf: true });
    if (problem) toast.error(problem); else onUpload(file);
  };
  const status = cert || number ? 'Credentials added' : 'Optional — add a certificate or a license number';
  return (
    <li className="space-y-3 rounded-lg border border-white/10 p-3" data-testid={`pp-license-${code}`}>
      <div className="flex flex-wrap items-center gap-2">
        <span className="text-sm font-semibold">{name}</span>
        <span className="rounded bg-white/10 px-1.5 py-0.5 font-mono text-[11px] text-white/70">{code}</span>
        <span className={`ml-1 flex items-center gap-1 text-xs ${cert || number ? 'text-emerald-400' : 'text-white/45'}`}>
          {(cert || number) && <CheckCircle2 className="h-3.5 w-3.5" />}{status}
        </span>
        <button type="button" disabled={busy} onClick={onRemoveState} className="ml-auto rounded-lg border border-white/15 px-2.5 py-1 text-[11px] font-bold uppercase text-white/70 hover:bg-white/5 disabled:opacity-50">
          Remove state
        </button>
      </div>

      <div className="grid items-stretch gap-3 sm:grid-cols-[1fr_auto_1fr]">
        {/* Option A: file */}
        <div className="flex items-center gap-3">
          <div className="flex h-16 w-24 shrink-0 items-center justify-center overflow-hidden rounded border border-dashed border-white/20 bg-white/5">
            {thumb ? <img src={thumb} alt={`${name} license`} className="h-full w-full object-cover" />
              : isPdf ? <FileText className="h-6 w-6 text-white/50" aria-label="PDF certificate" />
                : <ImagePlus className="h-5 w-5 text-white/30" />}
          </div>
          <div className="flex flex-col items-start gap-1.5">
            <button type="button" disabled={busy} onClick={() => input.current?.click()} data-testid={`pp-license-upload-${code}`} className="rounded-lg border border-civix-purple/50 px-3 py-1.5 text-[11px] font-bold uppercase text-civix-purpleSoft hover:bg-civix-purple/10 disabled:opacity-50">
              {cert ? 'Replace certificate' : 'Upload License Certificate (Optional)'}
            </button>
            <span className="text-[10px] text-white/40">PNG, JPG or PDF · max 5MB</span>
            {cert && (
              <button type="button" disabled={busy} onClick={onDelete} data-testid={`pp-license-delete-${code}`} className="inline-flex items-center gap-1 text-[11px] font-semibold text-rose-300 hover:text-rose-200 disabled:opacity-50">
                <Trash2 className="h-3 w-3" /> Remove certificate
              </button>
            )}
          </div>
          <input ref={input} type="file" accept={ACCEPT} className="hidden" onChange={(e) => { pick(e.target.files?.[0]); e.target.value = ''; }} />
        </div>

        <div className="flex items-center justify-center text-[11px] font-bold tracking-widest text-white/35" aria-hidden="true">— OR —</div>

        {/* Option B: number */}
        <label className="block">
          <span className="mb-1 block text-[10px] font-semibold uppercase tracking-wider text-white/50">State License # (or NPN)</span>
          <input
            value={number}
            onChange={(e) => onNumber(e.target.value)}
            maxLength={30}
            placeholder={npn ? `e.g. ${npn}` : 'License number'}
            data-testid={`pp-license-number-${code}`}
            className="w-full rounded-lg border border-white/10 bg-white/5 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-civix-purple/60 focus:outline-hidden"
          />
          {npn && number !== npn && (
            <button type="button" onClick={() => onNumber(npn)} data-testid={`pp-license-use-npn-${code}`} className="mt-1 text-[11px] text-civix-purpleSoft underline">
              Use my NPN ({npn})
            </button>
          )}
          <span className="mt-0.5 block text-[10px] text-white/40">Some states (like Montana) issue a license ID that differs from your NPN — enter it here.</span>
        </label>
      </div>
    </li>
  );
}

/** Step 4b — per selected state: certificate file OR license number (both optional). */
export default function LicenseUploads({ ctx }) {
  const { page, patch, catalog, assets, rev, busy, upload, remove, save } = ctx;
  const certOf = (code) => assets.licenses.find((l) => l.state === code);
  const nameOf = (c) => catalog?.states?.find((s) => s.code === c)?.name || c;
  const npn = (page.nationalProducerNumber || '').trim();
  const withCreds = page.licenseStates.filter((c) => certOf(c) || licenseNumberOf(page, c)).length;

  async function removeState(code) {
    const licenseStates = page.licenseStates.filter((c) => c !== code);
    patch({ licenseStates, stateLicenses: page.stateLicenses.filter((x) => x.state !== code) });
    // Persist right away so the server also drops that state's certificate.
    await save({ quiet: true, override: { licenseStates, stateLicenses: page.stateLicenses.filter((x) => x.state !== code) } });
  }

  return (
    <div className="space-y-4">
      <div>
        <h3 className="text-lg font-semibold">Your licenses</h3>
        <p className="text-xs text-white/60">For each state, add a copy of the license <b>or</b> type the license number — whichever you have. Both are optional; you can continue now and add them later.</p>
      </div>
      <NiprGuide defaultOpen />
      <div className="text-sm text-white/80" data-testid="pp-license-progress">{withCreds} of {page.licenseStates.length} states have credentials added</div>
      {page.licenseStates.length === 0 && <p className="text-sm text-amber-300">No states selected — go back and choose at least one.</p>}
      <ul className="space-y-2">
        {page.licenseStates.map((code) => (
          <LicenseRow
            key={code}
            code={code}
            name={nameOf(code)}
            cert={certOf(code)}
            npn={npn}
            number={licenseNumberOf(page, code)}
            rev={rev}
            busy={busy}
            onNumber={(v) => patch({ stateLicenses: setLicenseNumber(page.stateLicenses, code, v) })}
            onUpload={(f) => upload('license', f, code)}
            onDelete={() => remove('license', code)}
            onRemoveState={() => removeState(code)}
          />
        ))}
      </ul>
    </div>
  );
}
